import { GlobalOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { integrationRegistry } from "@p0security/shared/integrations/registry";
import { isPermissionType } from "@p0security/shared/types/permission";
import {
  Filter,
  IntegrationResourceRule,
} from "@p0security/shared/types/policy/types";
import { App, Popover, Space } from "@p0security/ui";
import { widetype } from "@p0security/util/collections";
import { isKeyOf } from "@p0security/util/types/is";
import { AllIntegrationLogos } from "components/Integrations/IntegrationLogo";
import {
  ANY_ACCESS_TYPE_ICON,
  accessTypeIcon,
} from "components/Jit/Policy/accessTypeIcons";
import {
  accessTypeOptionsByResource,
  filterTypeOptionsByResource,
  policyResourcePickerHiddenServices,
} from "components/Jit/Policy/constants";
import { EditableList } from "components/common/EditableList";
import { FormRow, RowLabel } from "components/common/FormRow";
import { PillSelect, PillSelectOption } from "components/common/PillSelect";
import { capitalize, noop, sortBy } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { keyLabelObjectToOptions } from "utils/antd-utilities";

import { PolicyEditorContext } from "../../store/RoutingRuleEditorContext";
import { getResourceFilterEntries } from "../../utils";
import { EditorBackButton } from "../EditorBackButton";
import { ResourceFilterPreview } from "../previews/ResourceFilterPreview";
import { ResourceFilterEditor } from "./ResourceFilterEditor";

type ResourceSelection = string;

const ANY_RESOURCE_OPTION: PillSelectOption<ResourceSelection> = {
  icon: <GlobalOutlined />,
  label: "Any resource",
  description: "Match every resource the identity could access",
  searchText: "any resource",
  value: "any",
};

// `integrationLabels` carries a few entries (e.g. "aws-rds", "agentic") that have
// no corresponding entry in the rule type union or policy configs. Filter to
// services that actually have access-type config so the pill can't surface a
// selection that crashes downstream lookups.
const resourceSelectionOptions: PillSelectOption<ResourceSelection>[] = [
  ANY_RESOURCE_OPTION,
  ...sortBy(
    widetype
      .entries(integrationRegistry)
      .filter(([value]) => {
        const service = value as IntegrationResourceRule["service"];
        return (
          service in accessTypeOptionsByResource &&
          !policyResourcePickerHiddenServices.has(service)
        );
      })
      .map(([value, { label }]) => ({
        icon: AllIntegrationLogos[value],
        label,
        searchText: label,
        value: value as IntegrationResourceRule["service"],
      })),
    "label"
  ),
];

const integrationAccessTypeOptions = (
  integration: string
): PillSelectOption<string>[] => {
  if (!isKeyOf(accessTypeOptionsByResource, integration)) return [];
  const perService = accessTypeOptionsByResource[integration];
  return [
    { icon: ANY_ACCESS_TYPE_ICON, label: "Any access type", value: "any" },
    ...(perService?.map((key) => ({
      icon: accessTypeIcon(key),
      label: capitalize(key),
      value: key,
    })) ?? []),
  ];
};

type Props = {
  onResourceFilterEditInProgress: (editing: boolean) => void;
};

export const ResourceEditor: React.FC<Props> = ({
  onResourceFilterEditInProgress,
}) => {
  const { resource, setResource, setResourceAccessType, removeResourceFilter } =
    useContext(PolicyEditorContext);
  const { modal } = App.useApp();

  const currentSelection: ResourceSelection =
    resource?.type === "integration" ? resource.service : "any";

  const [isAddingNewFilter, setIsAddingNewFilter] = useState(false);
  const [editingFilterIndex, setEditingFilterIndex] = useState<number | null>(
    null
  );

  const [localFilterType, setLocalFilterType] = useState<string>();
  const [localFilterEffect, setLocalFilterEffect] =
    useState<Filter["effect"]>();
  const [localFilterPattern, setLocalFilterPattern] = useState<string>();
  const [localFilterKey, setLocalFilterKey] = useState<string>();
  const [localFilterValue, setLocalFilterValue] = useState<boolean>();

  // Switching service wipes the rule's filters. Clear filter-editing state in
  // the same batch as the resource change so the conditional filter editor —
  // and the `resourceFilterDirty` memo that destructures filterEntries[index]
  // — never sees a stale `editingFilterIndex` pointing at a wiped filter.
  // TODO: migrate filter-editing state to `useForm` so we can drop these
  // manual `useState` hooks and the bespoke reset logic.
  const resetFilterEditingState = useCallback(() => {
    setIsAddingNewFilter(false);
    setEditingFilterIndex(null);
    setLocalFilterType(undefined);
    setLocalFilterEffect(undefined);
    setLocalFilterPattern(undefined);
    setLocalFilterKey(undefined);
    setLocalFilterValue(undefined);
    onResourceFilterEditInProgress(false);
  }, [onResourceFilterEditInProgress]);

  const handleSelectResource = useCallback(
    (next: ResourceSelection) => {
      if (next === currentSelection) return;
      const apply = () => {
        resetFilterEditingState();
        if (next === "any") {
          setResource({ type: "any" });
        } else {
          setResource({
            type: "integration",
            service: next,
            accessType: "any",
            filters: {},
          });
        }
      };

      const hasFilters = getResourceFilterEntries(resource).length > 0;
      if (hasFilters) {
        const target =
          next === "any"
            ? "Any resource"
            : isPermissionType(next)
              ? integrationRegistry[next]?.label
              : undefined;
        modal.confirm({
          title: "Discard resource filters?",
          content: `Switching to "${target}" removes the filters you've configured.`,
          okText: "Discard filters",
          okType: "danger",
          cancelText: "Keep editing",
          onOk: apply,
        });
        return;
      }
      apply();
    },
    [currentSelection, modal, resetFilterEditingState, resource, setResource]
  );

  const filterEntries = useMemo(
    () => getResourceFilterEntries(resource),
    [resource]
  );

  const filterListItems = useMemo(
    () => filterEntries.map(([type, value]) => ({ type, value })),
    [filterEntries]
  );

  const availableFilterTypes = useMemo(() => {
    if (resource?.type !== "integration") {
      return [];
    }
    const { filters, service } = resource;
    const used = filters ? Object.keys(filters) : [];
    if (!isKeyOf(filterTypeOptionsByResource, service)) return [];
    const options = filterTypeOptionsByResource[service];
    if (!options) return [];
    return keyLabelObjectToOptions(options).filter(
      ({ value }) => !used.includes(value)
    );
  }, [resource]);

  const resourceFilterDirty = useMemo(() => {
    if (resource?.type !== "integration") {
      return false;
    }
    if (isAddingNewFilter) {
      return (
        !!localFilterType ||
        !!localFilterEffect ||
        !!localFilterPattern ||
        !!localFilterKey ||
        localFilterValue !== undefined
      );
    }
    if (editingFilterIndex !== null && resource.filters) {
      const entry = filterEntries[editingFilterIndex];
      if (!entry) return false;
      const [fType, f] = entry;
      if (fType !== localFilterType || f?.effect !== localFilterEffect)
        return true;

      return (
        fType !== localFilterType ||
        f?.effect !== localFilterEffect ||
        (f.effect !== "removeAll" &&
          "key" in f &&
          (f.pattern !== localFilterPattern || f.key !== localFilterKey)) ||
        ("value" in f && f.value !== localFilterValue)
      );
    }
    return false;
  }, [
    resource,
    isAddingNewFilter,
    editingFilterIndex,
    localFilterType,
    localFilterEffect,
    localFilterPattern,
    localFilterKey,
    localFilterValue,
    filterEntries,
  ]);

  const handleStartAddingNewFilter = useCallback(() => {
    setLocalFilterType(undefined);
    setLocalFilterEffect(undefined);
    setLocalFilterPattern(undefined);
    setLocalFilterKey(undefined);
    setIsAddingNewFilter(true);
    onResourceFilterEditInProgress(true);
  }, [setIsAddingNewFilter, onResourceFilterEditInProgress]);

  const handleStartEditingFilter = useCallback(
    (index: number) => {
      const [fType, f] = filterEntries[index];
      setLocalFilterType(fType);
      setLocalFilterEffect(f.effect);
      if (f.effect !== "removeAll") {
        if ("value" in f) {
          setLocalFilterValue(f.value);
        } else {
          setLocalFilterPattern(f.pattern);
          setLocalFilterKey(f.key);
        }
      } else {
        setLocalFilterPattern(undefined);
        setLocalFilterKey(undefined);
        setLocalFilterValue(undefined);
      }
      setEditingFilterIndex(index);
      onResourceFilterEditInProgress(true);
    },
    [filterEntries, setEditingFilterIndex, onResourceFilterEditInProgress]
  );

  const handleBackToList = useCallback(() => {
    setIsAddingNewFilter(false);
    setEditingFilterIndex(null);
    onResourceFilterEditInProgress(false);
  }, [
    setEditingFilterIndex,
    setIsAddingNewFilter,
    onResourceFilterEditInProgress,
  ]);

  const localFilterContextValue = useMemo(
    () => ({
      localFilterType,
      setLocalFilterType,
      localFilterEffect,
      setLocalFilterEffect,
      localFilterKey,
      setLocalFilterKey,
      localFilterPattern,
      setLocalFilterPattern,
      localFilterValue,
      setLocalFilterValue,
      isDirty: resourceFilterDirty,
      isNew: isAddingNewFilter,
      onSave: handleBackToList,
    }),
    [
      localFilterType,
      localFilterEffect,
      localFilterKey,
      localFilterPattern,
      localFilterValue,
      resourceFilterDirty,
      isAddingNewFilter,
      handleBackToList,
    ]
  );

  const handleRemoveFilter = useCallback(
    (index: number) => {
      const [fType, f] = filterEntries[index];
      if (fType && f) {
        removeResourceFilter(fType);
      }
    },
    [removeResourceFilter, filterEntries]
  );

  const renderResourceFilterPreview = useCallback(
    (filter: { value: Filter; type: string }) => (
      <ResourceFilterPreview filter={filter.value} filterType={filter.type} />
    ),
    []
  );

  const accessTypeOptions = useMemo(() => {
    if (resource?.type !== "integration") {
      return [];
    }
    return integrationAccessTypeOptions(resource.service);
  }, [resource]);

  return (
    <Space direction="vertical" size="small" className="w-full">
      <FormRow>
        <PillSelect<ResourceSelection>
          options={resourceSelectionOptions}
          value={currentSelection}
          onChange={handleSelectResource}
          placeholder="Select a resource"
          searchable
        />
      </FormRow>
      {resource?.type === "integration" && (
        <>
          <FormRow>
            <RowLabel width={20}>Access type</RowLabel>
            <PillSelect
              value={resource.accessType ?? "any"}
              options={accessTypeOptions}
              onChange={setResourceAccessType}
            />
            {(!resource.accessType || resource.accessType === "any") && (
              <Popover
                content={
                  <div style={{ maxWidth: 300 }}>
                    Pick a more specific access type, especially when using
                    filters.
                  </div>
                }
                trigger="hover"
              >
                <InfoCircleOutlined className="text-[#595959]" />
              </Popover>
            )}
          </FormRow>
          <EditableList
            items={filterListItems}
            onEditItem={handleStartEditingFilter}
            onAddItem={handleStartAddingNewFilter}
            onDeleteItem={handleRemoveFilter}
            renderItem={renderResourceFilterPreview}
            addButtonText="Add New Resource Filter"
            addButtonDisabled={availableFilterTypes.length === 0}
            editing={isAddingNewFilter || editingFilterIndex !== null}
          />
          {(isAddingNewFilter || editingFilterIndex !== null) && (
            <LocalResourceFilterContext.Provider
              value={localFilterContextValue}
            >
              <ResourceFilterEditor
                cancelButton={
                  <EditorBackButton
                    onBack={handleBackToList}
                    isDirty={resourceFilterDirty}
                    isAdding={isAddingNewFilter}
                    name="Resource Filter"
                  />
                }
                resource={resource}
                filterType={
                  editingFilterIndex !== null
                    ? filterEntries[editingFilterIndex][0]
                    : undefined
                }
              />
            </LocalResourceFilterContext.Provider>
          )}
        </>
      )}
    </Space>
  );
};

type LocalResourceFilter = {
  localFilterType: string | undefined;
  setLocalFilterType: (type: string) => void;
  localFilterEffect: Filter["effect"] | undefined;
  setLocalFilterEffect: (effect: Filter["effect"]) => void;
  localFilterKey: string | undefined;
  setLocalFilterKey: (key: string) => void;
  localFilterPattern: string | undefined;
  setLocalFilterPattern: (pattern: string) => void;
  localFilterValue: boolean | undefined;
  setLocalFilterValue: (value: boolean | undefined) => void;
  isDirty: boolean;
  isNew: boolean;
  onSave: () => void;
};

export const LocalResourceFilterContext = createContext<LocalResourceFilter>({
  localFilterType: undefined,
  setLocalFilterType: noop,
  localFilterEffect: undefined,
  setLocalFilterEffect: noop,
  localFilterKey: undefined,
  setLocalFilterKey: noop,
  localFilterPattern: undefined,
  setLocalFilterPattern: noop,
  localFilterValue: undefined,
  setLocalFilterValue: noop,
  isDirty: false,
  isNew: false,
  onSave: noop,
});
